import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { creators as DebtorsActions } from '../../../Reducers/Reporting/Debtors';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import YearSelector from '../../../Common/Selectors/YearSelector';
import DebtorsDaysChart from './DebtorsDaysChart';
import AgeingChart from './AgeingChart';
import ItemsChart from './ItemsChart';
import ProjectChart from './ProjectChart';
import { financialMonth } from '../../../Utils/Functions';
import appdata from '../../../Services/appdata';

const Debtors = ({
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  selectedCompany,
  bubbleFlag
}) => {
  const dispatch = useDispatch();

  const debtorsSummary = useSelector(
    (state) => state?.Reporting?.Summary?.reportingSummaries?.debtors ?? '',
  );
  const summaryData = useSelector(
    (state) => state?.Reporting?.debtors?.summaryData ?? [],
  );
  const detailData = useSelector(
    (state) => state?.Reporting?.debtors?.detailData ?? [],
  );
  const target = useSelector((state) => state?.Reporting?.KPI?.target ?? null);
  const debtorsSetting = useSelector(
    (state) => state?.Reporting?.reportSetting?.Debtors ?? null,
  );
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const [resize, setResize] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [label, setLabel] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedDaysRanges, setSelectedDaysRanges] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItemStacks, setSelectedItemStacks] = useState([]);
  const [projectFilter, setProjectFilter] = useState('Director');

  const isMountedRef = useRef(true);

  const getDebtorsSummary = useCallback((selectedYears, defaultYear, defaultMonth) => {
    dispatch(DebtorsActions.debtorsSummaryRequest({
      selectedYears,
      defaultYear,
      defaultMonth,
    }));
  }, [dispatch]);

  const getDebtorsDetail = useCallback((selectedYears, defaultYear, defaultMonth) => {
    dispatch(DebtorsActions.debtorsDetailRequest({
      selectedYears,
      defaultYear,
      defaultMonth,
    }));
  }, [dispatch]);

  const reportSettingUpdateRequest = useCallback((update_setting) => {
    dispatch(ReportSettingActions.reportSettingUpdateRequest(update_setting));
  }, [dispatch]);

  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
    }
  }, []);

  const handleFilter = useCallback(async (event) => {
    if (event.selectedYears !== undefined) setSelectedYears(event.selectedYears);
    if (event.label !== undefined) setLabel(event.label);
    if (event.selectedMonths !== undefined) setSelectedMonths(event.selectedMonths);
    if (event.selectedDaysRanges !== undefined) setSelectedDaysRanges(event.selectedDaysRanges);
    if (event.selectedItems !== undefined) setSelectedItems(event.selectedItems);
    if (event.selectedItemStacks !== undefined) setSelectedItemStacks(event.selectedItemStacks);

    if (event.selectedItems && event.selectedItems.length > 0) {
      appdata.setLocalItem('dtSelectedItems', event.selectedItems);
    }
    if (event.selectedItemStacks && event.selectedItemStacks.length > 0) {
      appdata.setLocalItem('dtSelectedItemStacks', event.selectedItemStacks);
    }

    if (event.projectFilter !== undefined && projectFilter !== event.projectFilter) {
      setProjectFilter(event.projectFilter);
      await reportSettingUpdateRequest({
        Debtors: JSON.stringify({
          projectFilter: event.projectFilter,
        }),
      });
    }
  }, [projectFilter, reportSettingUpdateRequest]);

  const handleYear = useCallback(async (event) => {
    if (event.selectedYears.length === 0) {
      toast.warning('Please select the year.');
      return;
    }

    var dyear =
      defaultMonth < defaultStartMonth ? defaultYear - 1 : defaultYear;
    var dmonth = financialMonth(defaultMonth, defaultStartMonth);

    getDebtorsSummary(event.selectedYears, dyear, dmonth);
    getDebtorsDetail(event.selectedYears, dyear, dmonth);
    handleFilter(event);
  }, [defaultMonth, defaultStartMonth, defaultYear, getDebtorsSummary, getDebtorsDetail, handleFilter]);

  useEffect(() => {
    isMountedRef.current = true;
    window.addEventListener('resize', onResize);

    const initializeComponent = async () => {
      const initialSelectedItems = appdata.getLocalItem('dtSelectedItems') || [];
      const initialSelectedItemStacks = appdata.getLocalItem('dtSelectedItemStacks') || [];

      setSelectedItems(initialSelectedItems);
      setSelectedItemStacks(initialSelectedItemStacks);

      if (debtorsSetting) {
        const parsedSetting = JSON.parse(debtorsSetting);
        setProjectFilter(parsedSetting.projectFilter);
      } else {
        await reportSettingUpdateRequest({
          Debtors: JSON.stringify({
            projectFilter: 'Director',
          }),
        });
      }
    };

    initializeComponent();

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const triggerDebtorsRequests = useCallback(async () => {
    const dyear =
      defaultMonth < defaultStartMonth ? defaultYear - 1 : defaultYear;
    const dmonth = financialMonth(defaultMonth, defaultStartMonth);

    await getDebtorsSummary(
      [defaultYear],
      dyear,
      dmonth,
    );
    await getDebtorsDetail(
      [defaultYear],
      dyear,
      dmonth,
    );
  }, [
    defaultYear,
    defaultMonth,
    defaultStartMonth,
    getDebtorsSummary,
    getDebtorsDetail,
  ]);

  useEffect(() => {
    if (!isMountedRef.current) return;

    const updateForPropChanges = async () => {
      setSelectedYears([defaultYear]);
      setLabel(defaultYear.toString());

      if (debtorsSetting) {
        const parsedSetting = JSON.parse(debtorsSetting);
        await reportSettingUpdateRequest({
          Debtors: JSON.stringify({
            projectFilter: parsedSetting.projectFilter,
          }),
        });
      }

      if (!lastFetchedCompany || !lastFetchedDimDate) {
        await triggerDebtorsRequests();
      }
    };

    updateForPropChanges();
  }, [
    defaultYear,
    debtorsSetting,
    reportSettingUpdateRequest,
    triggerDebtorsRequests,
    lastFetchedCompany,
    lastFetchedDimDate,
  ]);

  useEffect(() => {
    if (!isMountedRef.current) return;

    const run = async () => {
      const valuesChanged =
        lastFetchedCompany !== selectedCompany ||
        lastFetchedDimDate !== defaultDimDate;

      if (valuesChanged && lastFetchedCompany && lastFetchedDimDate) {
        triggerDebtorsRequests();
      }
    };

    run();
  }, [
    selectedCompany,
    defaultDimDate,
    lastFetchedCompany,
    lastFetchedDimDate,
    triggerDebtorsRequests,
  ]);

  const { maxClientNameLength, maxProjectNameLength } = React.useMemo(() => {
    let clientNameLengths = [],
        maxClientNameLength = 0,
        projectNameLengths = [],
        maxProjectNameLength = 0;
    
    detailData.forEach((data) => {
      clientNameLengths.push(data.ClientName.length);
      projectNameLengths.push(data.ProjectName.length);
    });
    
    if (projectNameLengths.length > 0) {
      maxClientNameLength = Math.max(...clientNameLengths);
      maxProjectNameLength = Math.max(...projectNameLengths);
    }

    return { maxClientNameLength, maxProjectNameLength };
  }, [detailData]);

  return (
    <div style={{ height: '89vh' }}>
      <div className="wrapper" id="debtors_year_selector">
        <YearSelector
          selectedYears={selectedYears}
          label={label}
          onChange={handleYear}
        />
        <div className="right well"></div>
      </div>

      <div>
        <DebtorsDaysChart
          summaryData={summaryData}
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          defaultStartMonth={defaultStartMonth}
          handleFilter={handleFilter}
          target={target}
        />

        <div style={{ paddingTop: 20 }} id="debtors-middle-chart">
          <Grid container>
            <Grid size="grow" style={{ zIndex: 99 }}>
              <AgeingChart
                detailData={detailData}
                selectedYears={selectedYears}
                selectedDaysRanges={selectedDaysRanges}
                handleFilter={handleFilter}
              />
            </Grid>
            <Grid size="grow" style={{ zIndex: 99 }}>
              <ItemsChart
                detailData={detailData}
                selectedYears={selectedYears}
                selectedDaysRanges={selectedDaysRanges}
                selectedItemStacks={selectedItemStacks}
                selectedItems={selectedItems}
                projectFilter={projectFilter}
                handleFilter={handleFilter}
              />
            </Grid>
          </Grid>
        </div>
        <ProjectChart
          detailData={detailData}
          selectedYears={selectedYears}
          selectedDaysRanges={selectedDaysRanges}
          selectedItems={selectedItems}
          selectedItemStacks={selectedItemStacks}
          maxClientNameLength={maxClientNameLength}
          maxProjectNameLength={maxProjectNameLength}
          handleFilter={handleFilter}
        />
      </div>

      {bubbleFlag && debtorsSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={debtorsSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

Debtors.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number,
  selectedCompany: PropTypes.any,
};

export default Debtors;
